<script setup lang="ts">
import { ref } from "vue";
import { TuButton, ButtonSeverity } from "@tinyurldev/tu-ui-vue";

import { logError } from "@Resources/js/helpers/logger";
import XOverlay from "@Modules/shared/store/XOverlay";
import { useTranslation } from "@Resources/js/composables";
import type { Domain } from "@Modules/urls/Data/Models/DomainModel";
import PopoverOverhauling from "@Components/popover/PopoverOverhauling.vue";
import DomainInfoHeader from "@Modules/shared/Components/DomainInfoHeader.vue";

//
// Setup
const overlay = XOverlay();
const { __ } = useTranslation();

//
// Props
interface Props {
  domain: Domain;
  deleteDomain: (domain: string) => Promise<boolean>;
  restoreDomain: (domain: string) => Promise<boolean>;
}

const props = defineProps<Props>();

//
// Data
const isBusy = ref(false);

//
// Emit
interface Events {
  (e: "delete"): void;
  (e: "restore"): void;
}

const emit = defineEmits<Events>();

//
// Methods
function onDelete(): void {
  isBusy.value = true;

  props.deleteDomain(props.domain.domain)
    .then((result) => {
      if (result) {
        emit("delete");

        overlay.hide();
      }
    })
    .catch(() => { logError(`[DeleteDomainPopover][onDelete]: Domain ${props.domain.domain} could not be deleted.`) })
    .finally(() => { isBusy.value = false; });
}

function onRestore(): void {
  isBusy.value = true;

  props.restoreDomain(props.domain.domain)
    .then((result) => {
      if (result) {
        emit("restore");

        overlay.hide();
      }
    })
    .catch(() => { logError(`[DeleteDomainPopover][onRestore]: Domain ${props.domain.domain} could not be restored.`) })
    .finally(() => { isBusy.value = false; });
}

//
// Exposed Test Vars
defineExpose({ isBusy });
</script>

<template>
  <PopoverOverhauling @close="overlay.hide()">
    <template #title>
      {{ domain.isDeleted
        ? __("organization_domains_settings.delete_domain_popover.restore_title")
        : __("organization_domains_settings.delete_domain_popover.confirm_title")
      }}
    </template>

    <div class="delete-domain-popover">
      <DomainInfoHeader :domain="domain" />

      <template v-if="domain.isDeleted">
        <TuButton
          class="_restore"
          :label="__('organization_domains_settings.delete_domain_popover.restore_button')"
          :severity="ButtonSeverity.ALERT"
          :is-loading="isBusy"
          :is-disabled="isBusy"
          is-block
          @click.stop="onRestore"
        />
      </template>

      <template v-else>
        <p class="delete-domain-popover__warning">
          {{ __("organization_domains_settings.delete_domain_popover.warning_redirects") }}
        </p>
        <p
          v-if="!domain.isPurchased"
          class="delete-domain-popover__warning"
        >
          {{ __("organization_domains_settings.delete_domain_popover.warning_dns") }}
        </p>
        <p class="delete-domain-popover__question">
          {{ __("organization_domains_settings.delete_domain_popover.confirm_question") }}
        </p>

        <div class="delete-domain-popover__actions">
          <TuButton
            class="_confirm"
            is-block
            :label="__('organization_domains_settings.delete_domain_popover.confirm_button')"
            :severity="ButtonSeverity.ALERT"
            :is-loading="isBusy"
            :is-disabled="isBusy"
            @click.stop="onDelete"
          />
        </div>
      </template>
    </div>
  </PopoverOverhauling>
</template>

<style lang="scss" scoped>
@use "@tinyurldev/tu-ui-vue/scss/settings/typography.scss" as typography;
@use "@tinyurldev/tu-ui-vue/scss/settings/spacing.scss" as spacing;

.delete-domain-popover {
  display: flex;
  flex-flow: column;
  gap: spacing.$xxs;

  &__warning,
  &__question {
    margin: 0;

    font-size: typography.$base-font-size;
    font-weight: typography.$font-weight-medium;
    line-height: typography.$base-line-height;
  }

  &__actions {
    display: flex;
    gap: spacing.$xxs;
  }
}
</style>
